/**
 * The board, on one side or the other.
 *
 * Both sides run the same loop: hydrate, poll, queue local operations, flush
 * them, reconcile. The only differences are which endpoints they call and what
 * they may do besides drawing — which is why the loop lives in one hook here
 * and the two callers differ by a transport, not by a second implementation.
 *
 * Two rules earn their place:
 *
 * - A poll never overwrites work that has not been sent. Between a drag ending
 *   and the flush landing there is a window where the server's document is
 *   genuinely older than the screen; hydrating there makes a figure jump back.
 * - Operations are queued and collapsed rather than sent per frame. A drag
 *   emits a move per frame, and the batch limit is a hundred.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { invert } from "../domain/history";
import {
    applyLocally,
    collapseOperations,
    type Operation,
} from "../domain/operations";
import { SystemicMapAPI } from "../infrastructure/api/SystemicMap.api";
import { useBoardPresence, type BoardPresence } from "./useBoardPresence";
import type { MapState, ShareLink, SystemicMap } from "../domain/types";

/**
 * How often the board falls back to asking.
 *
 * The live channel says the moment something changes, so this is a safety net
 * rather than the mechanism — long enough not to be traffic, short enough that
 * a board whose socket is down still catches up on its own.
 */
export const POLL_INTERVAL_MS = 1500;
export const IDLE_POLL_INTERVAL_MS = 8000;
export const FLUSH_DELAY_MS = 400;

export type LoadStatus = "idle" | "loading" | "ready" | "error";

/** One step of your own history: what it did, and what takes it back. */
interface HistoryEntry {
    done: Operation[];
    undone: Operation[];
}

/**
 * How far back undo reaches.
 *
 * Deep enough for a session's worth of work, bounded so a long board does not
 * hold every edit ever made in memory.
 */
const MAX_HISTORY = 50;
/**
 * `error` is "we could not reach the server, still trying".
 * `rejected` is "the server refused this change, and it has been dropped" —
 * a different sentence, because one of them ends and the other does not.
 */
export type SaveStatus = "idle" | "saving" | "saved" | "error" | "rejected";

/** What a board needs from whichever endpoint pair is behind it. */
export interface BoardTransport<T extends { state: MapState; version: number }> {
    read: () => Promise<T>;
    write: (operations: Operation[]) => Promise<T>;
}

export interface BoardChannel<T extends { state: MapState; version: number }> {
    board: T | null;
    /** True while the live channel is carrying traffic. */
    live?: boolean;
    state: MapState | null;
    status: LoadStatus;
    saveStatus: SaveStatus;
    /** Queue operations: shown at once, sent shortly after. */
    submit: (operations: Operation[]) => void;
    /** Take back the last thing *you* did, and put it back again. */
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    /** Send whatever is queued now (leaving the page, taking a snapshot). */
    flush: () => Promise<void>;
    refresh: () => Promise<void>;
}

/**
 * Whether the server refused the content, as opposed to never hearing it.
 *
 * A 4xx that is not a timeout or a rate limit is a judgement about the batch
 * itself; anything else — no response, a 5xx, a dropped connection — is worth
 * trying again.
 */
const wasRefused = (error: unknown): boolean => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (typeof status !== "number") return false;
    return status >= 400 && status < 500 && status !== 408 && status !== 429;
};

export function useBoardChannel<T extends { state: MapState; version: number }>(
    transport: BoardTransport<T> | null,
    /** Optional live channel. Without it the board falls back to polling. */
    presence?: BoardPresence,
): BoardChannel<T> {
    const [board, setBoard] = useState<T | null>(null);
    const [state, setState] = useState<MapState | null>(null);
    const [status, setStatus] = useState<LoadStatus>("idle");
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
    /**
     * What you did, and what would take it back.
     *
     * Only your own actions: the other person's edits are not yours to undo,
     * and a stack that held them would take back work you never did.
     */
    const [past, setPast] = useState<HistoryEntry[]>([]);
    const [future, setFuture] = useState<HistoryEntry[]>([]);
    // Read by submit, which must invert against the board as it stands rather
    // than as it was when the callback was made.
    const stateRef = useRef<MapState | null>(null);

    const queue = useRef<Operation[]>([]);
    const inFlight = useRef(false);
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Held in a ref as well as in state so the poll and the flush read the
    // same transport without re-subscribing every render.
    const transportRef = useRef(transport);
    transportRef.current = transport;

    const hydrate = useCallback((next: T) => {
        setBoard(next);
        setState(next.state);
        stateRef.current = next.state;
    }, []);

    const refresh = useCallback(async () => {
        const current = transportRef.current;
        if (!current) return;
        // Work not yet sent is newer than anything the server can answer with.
        if (inFlight.current || queue.current.length > 0) return;
        try {
            hydrate(await current.read());
            setStatus("ready");
        } catch {
            setStatus("error");
        }
    }, [hydrate]);

    const send = useCallback(async () => {
        const current = transportRef.current;
        if (!current || inFlight.current) return;
        const batch = collapseOperations(queue.current);
        if (batch.length === 0) return;

        queue.current = [];
        inFlight.current = true;
        setSaveStatus("saving");
        try {
            const next = await current.write(batch);
            hydrate(next);
            // Tell the other side now rather than letting them find out on
            // their next poll.
            presence?.reportChange(next.version);
            setSaveStatus("saved");
            if (savedTimer.current) clearTimeout(savedTimer.current);
            savedTimer.current = setTimeout(() => setSaveStatus("idle"), 1500);
        } catch (error) {
            if (wasRefused(error)) {
                // The server will never accept this batch, so putting it back
                // would retry it for ever — and every later edit queues behind
                // it and fails with it. The board stops saving entirely and
                // says "still trying" until the tab is closed. Dropped
                // instead, and said plainly.
                setSaveStatus("rejected");
            } else {
                // A transport failure is worth retrying: the alternative is a
                // therapist watching an element they placed quietly disappear.
                queue.current = [...batch, ...queue.current];
                setSaveStatus("error");
            }
        } finally {
            inFlight.current = false;
        }
    }, [hydrate, presence]);

    const apply = useCallback(
        (operations: Operation[], remember: "record" | "silent" = "record") => {
            if (operations.length === 0) return;
            // A new edit clears a previous refusal: the board is usable again,
            // and leaving the warning up would say otherwise.
            setSaveStatus((current) => (current === "rejected" ? "idle" : current));

            if (remember === "record" && stateRef.current) {
                const undone = invert(stateRef.current, operations);
                if (undone.length > 0) {
                    setPast((current) => [
                        ...current.slice(-MAX_HISTORY + 1),
                        { done: operations, undone },
                    ]);
                    setFuture([]);
                }
            }

            queue.current = [...queue.current, ...operations];
            setState((current) => {
                const next = current ? applyLocally(current, operations) : current;
                stateRef.current = next;
                return next;
            });
            if (flushTimer.current) clearTimeout(flushTimer.current);
            flushTimer.current = setTimeout(() => void send(), FLUSH_DELAY_MS);
        },
        [send],
    );

    const submit = useCallback(
        (operations: Operation[]) => apply(operations),
        [apply],
    );

    const undo = useCallback(() => {
        setPast((current) => {
            const entry = current[current.length - 1];
            if (!entry) return current;
            apply(entry.undone, "silent");
            setFuture((ahead) => [...ahead, entry]);
            return current.slice(0, -1);
        });
    }, [apply]);

    const redo = useCallback(() => {
        setFuture((current) => {
            const entry = current[current.length - 1];
            if (!entry) return current;
            apply(entry.done, "silent");
            setPast((behind) => [...behind, entry]);
            return current.slice(0, -1);
        });
    }, [apply]);

    const flush = useCallback(async () => {
        if (flushTimer.current) clearTimeout(flushTimer.current);
        await send();
    }, [send]);

    useEffect(() => {
        if (!transport) return;
        setStatus("loading");
        void refresh();
        // Slower once the socket is up: it tells us the moment anything
        // changes, so the poll is only there for when it is not.
        const interval = setInterval(
            () => void refresh(),
            presence?.live ? IDLE_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
        );
        return () => clearInterval(interval);
        // `transport` is rebuilt per render by its callers; depending on it
        // would restart the poll every render. The ref keeps it current.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Boolean(transport), refresh, presence?.live]);

    useEffect(
        () => () => {
            if (flushTimer.current) clearTimeout(flushTimer.current);
            if (savedTimer.current) clearTimeout(savedTimer.current);
        },
        [],
    );

    return {
        board,
        state,
        status,
        saveStatus,
        submit,
        undo,
        redo,
        canUndo: past.length > 0,
        canRedo: future.length > 0,
        flush,
        refresh,
        live: presence?.live,
    };
}

/** Build a stable transport from two functions that already close over ids. */
export function useTransport<T extends { state: MapState; version: number }>(
    ready: boolean,
    read: () => Promise<T>,
    write: (operations: Operation[]) => Promise<T>,
): BoardTransport<T> | null {
    return useMemo(() => (ready ? { read, write } : null), [ready, read, write]);
}

/**
 * Therapist-side map: the board plus the management actions the patient does
 * not get — renaming, the share link, and pushing a snapshot into the record.
 */
export function useTherapistMap(mapId: string | undefined) {
    const [shareLink, setShareLink] = useState<ShareLink | null>(null);

    const read = useCallback(async () => {
        const { data } = await SystemicMapAPI.get(mapId!);
        return data;
    }, [mapId]);

    const write = useCallback(
        async (operations: Operation[]) => {
            const { data } = await SystemicMapAPI.applyOperations(mapId!, operations);
            return data;
        },
        [mapId],
    );

    const requestTicket = useMemo(
        () =>
            mapId
                ? async () => (await SystemicMapAPI.wsTicket(mapId)).data.ticket
                : null,
        [mapId],
    );
    const transport = useTransport<SystemicMap>(Boolean(mapId), read, write);
    // The socket tells the board to refetch; the board is created after it, so
    // the callback reaches it through a ref rather than a circular dependency.
    const refreshRef = useRef<() => void>(() => {});
    const presence = useBoardPresence(requestTicket, () => refreshRef.current());
    const channel = useBoardChannel<SystemicMap>(transport, presence);
    refreshRef.current = () => void channel.refresh();

    const createShareLink = useCallback(async () => {
        if (!mapId) return null;
        const { data } = await SystemicMapAPI.createShareLink(mapId);
        setShareLink(data);
        return data;
    }, [mapId]);

    const revokeShareLink = useCallback(async () => {
        if (!mapId) return;
        await SystemicMapAPI.revokeShareLink(mapId);
        setShareLink(null);
    }, [mapId]);

    /**
     * Put the map in the patient's own portal, read-only, or take it out.
     *
     * The board itself is refetched rather than patched locally: sharing is
     * the kind of thing a therapist wants to see confirmed, not assumed.
     */
    const setPatientSharing = useCallback(
        async (shared: boolean) => {
            if (!mapId) return;
            await SystemicMapAPI.setPatientSharing(mapId, shared);
            await channel.refresh();
        },
        [mapId, channel],
    );

    const rename = useCallback(
        async (title: string) => {
            if (!mapId) return;
            await SystemicMapAPI.rename(mapId, title);
            await channel.refresh();
        },
        [mapId, channel],
    );

    const uploadSnapshot = useCallback(
        async (blob: Blob) => {
            if (!mapId) return;
            // Anything still queued belongs in the picture being filed.
            await channel.flush();
            await SystemicMapAPI.uploadSnapshot(mapId, blob);
            await channel.refresh();
        },
        [mapId, channel],
    );

    return {
        ...channel,
        presence,
        shareLink,
        createShareLink,
        revokeShareLink,
        setPatientSharing,
        rename,
        uploadSnapshot,
    };
}
